/**
 * Shared ECharts lifecycle + theme helpers for prototype cards.
 * Every chart prototype mounts the same way: init on the element, set option,
 * resize on window, dispose on unmount. Wrap that pattern once.
 */
import echarts from "./echarts";
import { COLORS } from "./designTokens";

type EChartsInstance = ReturnType<typeof echarts.init>;

/**
 * Mount an ECharts instance on `el`, register a window-resize handler, and
 * return a cleanup function that removes the listener and disposes the chart.
 *
 * The caller invokes `chart.setOption(...)` synchronously; the helper only owns
 * the lifecycle, not the option payload — each prototype's option object stays
 * local where it's easiest to read and tune.
 */
export function mountChart(el: HTMLDivElement, configure: (chart: EChartsInstance) => void): () => void {
  const chart = echarts.init(el);
  configure(chart);
  const resize = () => chart.resize();
  window.addEventListener("resize", resize);
  return () => {
    window.removeEventListener("resize", resize);
    chart.dispose();
  };
}

/** Tooltip styled to match swimset's dark navy palette. Drop into any chart's `tooltip` slot. */
export function darkTooltip(formatter?: unknown) {
  return {
    // confine the tooltip box INSIDE the chart container so it can't spill past
    // the viewport edge (ECharts defaults to confine:false → tooltips position
    // off-screen on a phone; this is the WebView "floating outside the app" bug).
    confine: true,
    backgroundColor: COLORS.navyRaised,
    borderColor: COLORS.navyRaised,
    textStyle: { color: COLORS.textPrimary, fontSize: 11 },
    ...(formatter !== undefined ? { formatter } : {}),
  };
}
